/**
 * Browser-side dispatcher for the local-fs tool group.
 *
 * The server declares the tools but does not execute them; the AI SDK
 * forwards each tool call to the client, which runs it against the
 * `FileSystemDirectoryHandle` the user granted for the chat. Results
 * are returned as plain JSON the SDK ships back as the tool result.
 *
 * Snapshots of pre-edit contents are written to IndexedDB before any
 * destructive op so the user can undo from the chat UI.
 *
 * @module file-agent/lib/fs-tools
 */

import { compileGitignore, isIgnored } from "./gitignore";
import { saveSnapshot } from "./fs-handle-store";

/** File extensions we refuse to read as text. */
const BINARY_EXTENSIONS = new Set([
  ".png", ".jpg", ".jpeg", ".gif", ".bmp", ".ico", ".webp", ".tiff",
  ".mp3", ".mp4", ".wav", ".avi", ".mov", ".mkv", ".flac", ".ogg", ".webm",
  ".zip", ".tar", ".gz", ".bz2", ".xz", ".7z", ".rar",
  ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx",
  ".exe", ".dll", ".so", ".dylib", ".bin", ".dat",
  ".woff", ".woff2", ".ttf", ".otf", ".eot",
  ".pyc", ".class", ".o", ".obj", ".wasm",
  ".sqlite", ".db",
]);

/** Files larger than this require a `start_line`/`end_line` to read. */
const LARGE_FILE_BYTES = 500_000;

/**
 * Result envelope returned to the agent. Errors are returned as
 * structured results (not thrown) so the LLM can react to them.
 */
export interface ToolResult<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
}

interface FileEntry {
  name: string;
  path: string;
  kind: "file" | "directory";
}

interface ListDirectoryResult {
  path: string;
  entries: FileEntry[];
}

interface ReadFileResult {
  path: string;
  content: string;
  totalLines: number;
  truncated: boolean;
}

interface WriteFileResult {
  path: string;
  /** Snapshot id the client writes to IndexedDB so the user can undo this edit. */
  snapshotId: string;
  bytesWritten: number;
}

/**
 * Verify (and re-request, if needed) read+write permission on a directory
 * handle. The browser drops permissions across page reloads, so we ask
 * again on first use after an attach. A user gesture is still required
 * the first time per session — callers should invoke this from a click.
 */
export async function ensureReadWritePermission(
  handle: FileSystemDirectoryHandle,
): Promise<boolean> {
  type Perm = "granted" | "denied" | "prompt";
  const opts = { mode: "readwrite" as const };
  const h = handle as unknown as {
    queryPermission(opts: { mode: "readwrite" }): Promise<Perm>;
    requestPermission(opts: { mode: "readwrite" }): Promise<Perm>;
  };
  const current = await h.queryPermission(opts);
  if (current === "granted") return true;
  const next = await h.requestPermission(opts);
  return next === "granted";
}

/** Normalize a user-supplied path (strip leading slashes, collapse `./`). */
function normalizePath(input: string): string {
  return input
    .replace(/^\.?\/+/, "")
    .replace(/\/+/g, "/")
    .replace(/\/$/, "");
}

function getExtension(path: string): string {
  const dot = path.lastIndexOf(".");
  return dot === -1 ? "" : path.slice(dot).toLowerCase();
}

/**
 * Walk into a path relative to the root handle. When `create` is true,
 * intermediate directories are created on the way down (used for
 * write/create paths). The terminal segment is returned via `parent` and
 * `name` so the caller can pick the right `getFileHandle`/`getDirectoryHandle`
 * call.
 */
async function resolveParent(
  root: FileSystemDirectoryHandle,
  path: string,
  create: boolean,
): Promise<{ parent: FileSystemDirectoryHandle; name: string }> {
  const parts = normalizePath(path).split("/").filter(Boolean);
  if (parts.length === 0) {
    throw new Error("Path is empty");
  }
  const name = parts.pop()!;
  let dir = root;
  for (const seg of parts) {
    dir = await dir.getDirectoryHandle(seg, { create });
  }
  return { parent: dir, name };
}

async function loadGitignore(root: FileSystemDirectoryHandle) {
  try {
    const handle = await root.getFileHandle(".gitignore");
    const file = await handle.getFile();
    return compileGitignore(await file.text());
  } catch {
    return [];
  }
}

async function detectBinary(file: File): Promise<boolean> {
  if (BINARY_EXTENSIONS.has(getExtension(file.name))) return true;
  // Sniff the first 4KB for null bytes — same heuristic git uses.
  const slice = await file.slice(0, 4096).arrayBuffer();
  const bytes = new Uint8Array(slice);
  for (const b of bytes) if (b === 0) return true;
  return false;
}

/**
 * read_file — return the file's text contents (or a slice).
 */
export async function readFile(
  root: FileSystemDirectoryHandle,
  args: { path: string; start_line?: number | null; end_line?: number | null },
): Promise<ToolResult<ReadFileResult>> {
  const path = normalizePath(args.path);
  try {
    const { parent, name } = await resolveParent(root, path, false);
    const handle = await parent.getFileHandle(name);
    const file = await handle.getFile();

    if (await detectBinary(file)) {
      return {
        success: false,
        message: `Cannot read binary file '${path}'.`,
      };
    }

    const text = await file.text();
    const lines = text.split("\n");
    const startLine = args.start_line ?? 1;
    const endLine = args.end_line ?? lines.length;
    let slice = text;
    let truncated = false;

    if (args.start_line != null || args.end_line != null) {
      slice = lines.slice(Math.max(0, startLine - 1), endLine).join("\n");
    } else if (file.size > LARGE_FILE_BYTES) {
      slice = lines.slice(0, 200).join("\n");
      truncated = true;
    }

    return {
      success: true,
      message: truncated
        ? `Read first 200 lines of '${path}' (file is large; pass start_line/end_line for more).`
        : `Read '${path}' (${lines.length} lines).`,
      data: {
        path,
        content: slice,
        totalLines: lines.length,
        truncated,
      },
    };
  } catch (err) {
    return {
      success: false,
      message: `Could not read '${path}': ${(err as Error).message}`,
    };
  }
}

/**
 * write_file — overwrite an existing file. Stores a pre-edit snapshot.
 */
export async function writeFile(
  root: FileSystemDirectoryHandle,
  chatId: string,
  toolCallId: string,
  args: { path: string; content: string },
): Promise<ToolResult<WriteFileResult>> {
  const path = normalizePath(args.path);
  try {
    const { parent, name } = await resolveParent(root, path, false);
    let previous: string;
    try {
      const handle = await parent.getFileHandle(name);
      previous = await (await handle.getFile()).text();
    } catch {
      return {
        success: false,
        message: `File '${path}' does not exist. Use create_file for new files.`,
      };
    }

    await saveSnapshot({
      chatId,
      snapshotId: toolCallId,
      path,
      kind: "write",
      previousContent: previous,
    });

    const handle = await parent.getFileHandle(name);
    const writable = await handle.createWritable();
    await writable.write(args.content);
    await writable.close();

    return {
      success: true,
      message: `Wrote '${path}' (${args.content.length} chars).`,
      data: {
        path,
        snapshotId: toolCallId,
        bytesWritten: args.content.length,
      },
    };
  } catch (err) {
    return {
      success: false,
      message: `Could not write '${path}': ${(err as Error).message}`,
    };
  }
}

/**
 * create_file — create a new file; fail if one exists.
 */
export async function createFile(
  root: FileSystemDirectoryHandle,
  chatId: string,
  toolCallId: string,
  args: { path: string; content: string },
): Promise<ToolResult<WriteFileResult>> {
  const path = normalizePath(args.path);
  try {
    const { parent, name } = await resolveParent(root, path, true);
    try {
      await parent.getFileHandle(name);
      return {
        success: false,
        message: `File '${path}' already exists. Use write_file to overwrite.`,
      };
    } catch {
      // Expected — file does not exist yet.
    }

    await saveSnapshot({
      chatId,
      snapshotId: toolCallId,
      path,
      kind: "create",
      previousContent: null,
    });

    const handle = await parent.getFileHandle(name, { create: true });
    const writable = await handle.createWritable();
    await writable.write(args.content);
    await writable.close();

    return {
      success: true,
      message: `Created '${path}'.`,
      data: {
        path,
        snapshotId: toolCallId,
        bytesWritten: args.content.length,
      },
    };
  } catch (err) {
    return {
      success: false,
      message: `Could not create '${path}': ${(err as Error).message}`,
    };
  }
}

/**
 * delete_file — remove a file. Stores a snapshot for undo.
 */
export async function deleteFile(
  root: FileSystemDirectoryHandle,
  chatId: string,
  toolCallId: string,
  args: { path: string },
): Promise<ToolResult<{ path: string; snapshotId: string }>> {
  const path = normalizePath(args.path);
  try {
    const { parent, name } = await resolveParent(root, path, false);
    let previous: string;
    try {
      const handle = await parent.getFileHandle(name);
      previous = await (await handle.getFile()).text();
    } catch {
      return {
        success: false,
        message: `File '${path}' does not exist.`,
      };
    }

    await saveSnapshot({
      chatId,
      snapshotId: toolCallId,
      path,
      kind: "delete",
      previousContent: previous,
    });

    await parent.removeEntry(name);

    return {
      success: true,
      message: `Deleted '${path}'.`,
      data: { path, snapshotId: toolCallId },
    };
  } catch (err) {
    return {
      success: false,
      message: `Could not delete '${path}': ${(err as Error).message}`,
    };
  }
}

/**
 * list_directory — list entries (one level deep) honouring `.gitignore`
 * + the built-in denylist.
 */
export async function listDirectory(
  root: FileSystemDirectoryHandle,
  args: { path: string },
): Promise<ToolResult<ListDirectoryResult>> {
  const path = normalizePath(args.path ?? "");
  try {
    const patterns = await loadGitignore(root);

    let dir: FileSystemDirectoryHandle = root;
    if (path) {
      const segments = path.split("/").filter(Boolean);
      for (const seg of segments) {
        dir = await dir.getDirectoryHandle(seg, { create: false });
      }
    }

    const entries: FileEntry[] = [];
    // FileSystemDirectoryHandle is async-iterable in Chromium.
    const iter = dir as unknown as AsyncIterable<[string, FileSystemHandle]>;
    for await (const [name, child] of iter) {
      const relativePath = path ? `${path}/${name}` : name;
      const isDir = child.kind === "directory";
      if (isIgnored(relativePath, patterns, isDir)) continue;
      entries.push({ name, path: relativePath, kind: isDir ? "directory" : "file" });
    }

    entries.sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === "directory" ? -1 : 1;
      return a.name.localeCompare(b.name);
    });

    return {
      success: true,
      message: `Listed '${path || "."}' (${entries.length} entries).`,
      data: { path, entries },
    };
  } catch (err) {
    return {
      success: false,
      message: `Could not list '${path || "."}': ${(err as Error).message}`,
    };
  }
}

interface SearchMatch {
  path: string;
  line: number;
  text: string;
}

interface SearchResult {
  pattern: string;
  scope: string;
  totalMatches: number;
  truncated: boolean;
  matches: SearchMatch[];
}

/** Files larger than this are skipped during search to keep walks bounded. */
const SEARCH_FILE_SIZE_LIMIT = 1_000_000;
/** Max characters returned per matching line so context stays small. */
const SEARCH_LINE_PREVIEW = 240;

/**
 * search_in_files — recursive grep across the attached folder. Skips
 * binaries and gitignored paths; caps results so a wide pattern doesn't
 * flood the LLM context.
 */
export async function searchInFiles(
  root: FileSystemDirectoryHandle,
  args: {
    pattern: string;
    path?: string;
    mode?: "literal" | "regex";
    case_sensitive?: boolean;
    max_results?: number;
  },
): Promise<ToolResult<SearchResult>> {
  const scope = normalizePath(args.path ?? "");
  const mode = args.mode ?? "literal";
  const caseSensitive = args.case_sensitive ?? false;
  const maxResults = Math.max(1, Math.min(args.max_results ?? 100, 500));

  let matcher: (line: string) => boolean;
  try {
    if (mode === "regex") {
      const re = new RegExp(args.pattern, caseSensitive ? "" : "i");
      matcher = (line) => re.test(line);
    } else {
      const needle = caseSensitive ? args.pattern : args.pattern.toLowerCase();
      matcher = caseSensitive
        ? (line) => line.includes(needle)
        : (line) => line.toLowerCase().includes(needle);
    }
  } catch (err) {
    return {
      success: false,
      message: `Invalid pattern: ${(err as Error).message}`,
    };
  }

  const patterns = await loadGitignore(root);

  let startDir: FileSystemDirectoryHandle = root;
  if (scope) {
    try {
      for (const seg of scope.split("/").filter(Boolean)) {
        startDir = await startDir.getDirectoryHandle(seg);
      }
    } catch (err) {
      return {
        success: false,
        message: `Could not open '${scope}': ${(err as Error).message}`,
      };
    }
  }

  const matches: SearchMatch[] = [];
  let totalMatches = 0;
  let truncated = false;

  const visit = async (
    dir: FileSystemDirectoryHandle,
    relPath: string,
  ): Promise<void> => {
    if (truncated) return;
    const iter = dir as unknown as AsyncIterable<[string, FileSystemHandle]>;
    for await (const [name, child] of iter) {
      if (truncated) return;
      const childPath = relPath ? `${relPath}/${name}` : name;
      const isDir = child.kind === "directory";
      if (isIgnored(childPath, patterns, isDir)) continue;
      if (isDir) {
        await visit(child as FileSystemDirectoryHandle, childPath);
        continue;
      }
      if (BINARY_EXTENSIONS.has(getExtension(name))) continue;
      const file = await (child as FileSystemFileHandle).getFile();
      if (file.size > SEARCH_FILE_SIZE_LIMIT) continue;
      // Cheap binary sniff — skip files whose first chunk has null bytes.
      const head = new Uint8Array(await file.slice(0, 1024).arrayBuffer());
      let nullByte = false;
      for (const b of head) {
        if (b === 0) {
          nullByte = true;
          break;
        }
      }
      if (nullByte) continue;

      const text = await file.text();
      const lines = text.split("\n");
      for (let i = 0; i < lines.length; i++) {
        if (!matcher(lines[i])) continue;
        totalMatches++;
        if (matches.length < maxResults) {
          matches.push({
            path: childPath,
            line: i + 1,
            text: lines[i].length > SEARCH_LINE_PREVIEW
              ? lines[i].slice(0, SEARCH_LINE_PREVIEW) + "…"
              : lines[i],
          });
        } else {
          truncated = true;
          return;
        }
      }
    }
  };

  try {
    await visit(startDir, scope);
  } catch (err) {
    return {
      success: false,
      message: `Search failed in '${scope || "."}': ${(err as Error).message}`,
    };
  }

  return {
    success: true,
    message: truncated
      ? `Found ${totalMatches}+ matches; returning first ${maxResults}. Narrow the pattern or pass max_results to see more.`
      : `Found ${totalMatches} match${totalMatches === 1 ? "" : "es"} in '${scope || "."}'.`,
    data: {
      pattern: args.pattern,
      scope: scope || ".",
      totalMatches,
      truncated,
      matches,
    },
  };
}

/**
 * Apply a snapshot to undo a previous edit. Returns the result envelope
 * so the calling UI can toast success/failure.
 */

/**
 * Map a tool name → handler. The chat's `onToolCall` looks up by name
 * and dispatches; everything else falls through to the SDK.
 */
export const LOCAL_FS_TOOL_NAMES = new Set([
  "local_fs_read_file",
  "local_fs_write_file",
  "local_fs_create_file",
  "local_fs_delete_file",
  "local_fs_list_directory",
  "local_fs_search_in_files",
]);

export interface DispatchContext {
  root: FileSystemDirectoryHandle;
  chatId: string;
  toolCallId: string;
}

/** Dispatch a tool call to the right handler. Always resolves with a ToolResult. */
export async function dispatchLocalFsToolCall(
  toolName: string,
  args: Record<string, unknown>,
  ctx: DispatchContext,
): Promise<ToolResult> {
  switch (toolName) {
    case "local_fs_read_file":
      return readFile(ctx.root, args as Parameters<typeof readFile>[1]);
    case "local_fs_write_file":
      return writeFile(
        ctx.root,
        ctx.chatId,
        ctx.toolCallId,
        args as Parameters<typeof writeFile>[3],
      );
    case "local_fs_create_file":
      return createFile(
        ctx.root,
        ctx.chatId,
        ctx.toolCallId,
        args as Parameters<typeof createFile>[3],
      );
    case "local_fs_delete_file":
      return deleteFile(
        ctx.root,
        ctx.chatId,
        ctx.toolCallId,
        args as Parameters<typeof deleteFile>[3],
      );
    case "local_fs_list_directory":
      return listDirectory(ctx.root, args as Parameters<typeof listDirectory>[1]);
    case "local_fs_search_in_files":
      return searchInFiles(ctx.root, args as Parameters<typeof searchInFiles>[1]);
    default:
      return { success: false, message: `Unknown local-fs tool: ${toolName}` };
  }
}
